import {fetchTopRepositories, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {CopilotIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, ButtonGroup, IconButton} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {useCopilotAgent} from '../../../hooks/use-copilot-agent'
import styles from './CopilotAgentModeButton.module.css'
import {CopilotRepositoryPicker} from './CopilotRepositoryPicker'

export type CopilotAgentModeButtonProps = {
  repo: {id: string; name: string; owner: {login: string}}
  issueId: number
  onError: (error: string) => void
}

export const CopilotAgentModeButton = ({repo, issueId, onError}: CopilotAgentModeButtonProps) => {
  const environment = useRelayEnvironment()
  const [topRepos, setTopRepos] = useState<RepositoryPickerRepository[] | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRepoId, setSelectedRepoId] = useState(repo.id)

  const anchorRef = useRef(null)

  const {codespaceCreationError, handleCopilotAgentModeSubmit} = useCopilotAgent({
    repoId: selectedRepoId,
    issueDatabaseId: issueId || 0,
  })

  useEffect(() => {
    if (codespaceCreationError) {
      onError(codespaceCreationError)
    }
  }, [codespaceCreationError, onError])

  useEffect(() => {
    if (selectedRepoId !== repo.id) {
      handleCopilotAgentModeSubmit()
    }
  }, [handleCopilotAgentModeSubmit, repo.id, selectedRepoId])

  const onRepoSelected = useCallback(
    (selectedRepo: {id: string; name: string; owner: {login: string}}) => {
      setSelectedRepoId(prevId => {
        if (prevId === selectedRepo.id) {
          handleCopilotAgentModeSubmit()
        }
        return selectedRepo.id
      })
      setIsOpen(false)
    },
    [setSelectedRepoId, setIsOpen, handleCopilotAgentModeSubmit],
  )

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      onError('')
      if (selectedRepoId) {
        handleCopilotAgentModeSubmit()
      }
    },
    [selectedRepoId, handleCopilotAgentModeSubmit, onError],
  )

  const handleIconClick = useCallback(async () => {
    onError('')
    if (!isLoading && !isOpen && !topRepos) {
      setIsLoading(true)

      const data = await fetchTopRepositories(environment)
      if (data) {
        setTopRepos(data)
        setIsLoading(false)
        setIsOpen(true)
      }
    } else {
      setIsOpen(!isOpen)
    }
  }, [environment, isLoading, isOpen, onError, topRepos])

  return (
    <>
      <ButtonGroup className={styles.buttonGroup}>
        <Button
          size="small"
          leadingVisual={CopilotIcon}
          onClick={handleButtonClick}
          data-testid="open-in-copilot-agent-button"
          block
        >
          {LABELS.development.copilot.openAgent}
        </Button>
        <IconButton
          size="small"
          loading={isLoading}
          ref={anchorRef}
          icon={TriangleDownIcon}
          aria-label="Select code repository"
          onClick={handleIconClick}
          aria-disabled={isLoading}
        />
      </ButtonGroup>
      <CopilotRepositoryPicker
        topRepos={topRepos || []}
        anchorRef={anchorRef}
        isOpen={isOpen}
        onSelect={onRepoSelected}
        onClose={() => isOpen && setIsOpen(false)}
      />
    </>
  )
}

try{ CopilotAgentModeButton.displayName ||= 'CopilotAgentModeButton' } catch {}